import React from 'react';
import { Database, Activity, CheckCircle, XCircle } from 'lucide-react';

const Overview = () => {
  const stats = [
    {
      title: "Total Migrations",
      value: "1,245",
      change: "+12% since last month",
      icon: Database,
      changeType: "positive"
    },
    {
      title: "Active Migrations",
      value: "42",
      change: "2 new this week",
      icon: Activity,
      changeType: "neutral"
    },
    {
      title: "Completed Migrations",
      value: "1,180",
      change: "+10% success rate",
      icon: CheckCircle,
      changeType: "positive"
    },
    {
      title: "Failed Migrations",
      value: "23",
      change: "-3% from last week",
      icon: XCircle,
      changeType: "negative"
    }
  ];

  const recentMigrations = [
    {
      id: 'VM-20240728-001',
      dbType: 'Vector Embedding',
      source: 'ChromaDB',
      destination: 'Qdrant',
      status: 'Completed',
      timestamp: '2024-07-28 10:30 AM'
    },
    {
      id: 'VM-20240728-002',
      dbType: 'Vector Index',
      source: 'Pinecone',
      destination: 'Weaviate',
      status: 'In Progress',
      timestamp: '2024-07-28 09:15 AM'
    },
    {
      id: 'VM-20240728-003',
      dbType: 'Metadata Sync',
      source: 'ChromaDB',
      destination: 'Pinecone',
      status: 'Pending',
      timestamp: '2024-07-28 08:00 AM'
    },
    {
      id: 'VM-20240725-004',
      dbType: 'Vector Embedding',
      source: 'Qdrant',
      destination: 'Weaviate',
      status: 'Failed',
      timestamp: '2024-07-25 04:45 PM'
    },
    {
      id: 'VM-20240725-005',
      dbType: 'Vector Index',
      source: 'Weaviate',
      destination: 'ChromaDB',
      status: 'Completed',
      timestamp: '2024-07-25 02:00 PM'
    },
    {
      id: 'VM-20240725-006',
      dbType: 'Metadata Sync',
      source: 'Pinecone',
      destination: 'Qdrant',
      status: 'Completed',
      timestamp: '2024-07-25 11:30 AM'
    }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'Completed':
        return 'text-emerald-400';
      case 'In Progress':
        return 'text-blue-400';
      case 'Pending':
        return 'text-amber-400';
      case 'Failed':
        return 'text-red-400';
      default:
        return 'text-gray-400';
    }
  };

  const getStatusBg = (status) => {
    switch (status) {
      case 'Completed':
        return 'bg-emerald-500/20';
      case 'In Progress':
        return 'bg-blue-500/20';
      case 'Pending':
        return 'bg-amber-500/20';
      case 'Failed':
        return 'bg-red-500/20';
      default:
        return 'bg-gray-500/20';
    }
  };

  const getChangeColor = (type) => {
    if (type === 'positive') return 'text-emerald-400';
    if (type === 'negative') return 'text-red-400';
    return 'text-blue-400';
  };

  return (
    <div className="space-y-6">
      {/* Page Title */}
      <h1 className="text-3xl font-bold text-white">Migration Overview</h1>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <div
              key={index}
              className="bg-[#141414] border border-gray-800 rounded-xl p-6 hover:border-gray-700 transition-all duration-300"
            >
              <div className="flex items-center justify-between mb-4">
                <span className="text-gray-400 text-sm font-medium">{stat.title}</span>
                <Icon className="w-5 h-5 text-gray-500" />
              </div>
              <div className="text-3xl font-bold text-white mb-2">{stat.value}</div>
              <div className={`text-xs ${getChangeColor(stat.changeType)}`}>{stat.change}</div>
            </div>
          );
        })}
      </div>

      {/* Analytics Section */}
      <h2 className="text-xl font-bold text-white mt-8">Migration Analytics</h2>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Success Rate Chart */}
        <div className="bg-[#141414] border border-gray-800 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-6">Migration Success Rate</h3>
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-400">Successful</span>
                <span className="text-sm font-medium text-white">95.2%</span>
              </div>
              <div className="h-12 bg-emerald-500 rounded"></div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-400">Failed</span>
                <span className="text-sm font-medium text-white">4.8%</span>
              </div>
              <div className="h-12 bg-red-400 rounded" style={{ width: '15%' }}></div>
            </div>
          </div>
        </div>

        {/* Pending Jobs Donut */}
        <div className="bg-[#141414] border border-gray-800 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-6">Pending Jobs by Database</h3>
          <div className="flex items-center justify-center h-64">
            <div className="relative w-48 h-48">
              <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                <circle
                  cx="50"
                  cy="50"
                  r="40"
                  fill="none"
                  stroke="#1f2937"
                  strokeWidth="20"
                />
                <circle
                  cx="50"
                  cy="50"
                  r="40"
                  fill="none"
                  stroke="#3b82f6"
                  strokeWidth="20"
                  strokeDasharray="75.4 251.2"
                  strokeDashoffset="0"
                />
                <circle
                  cx="50"
                  cy="50"
                  r="40"
                  fill="none"
                  stroke="#8b5cf6"
                  strokeWidth="20"
                  strokeDasharray="62.8 251.2"
                  strokeDashoffset="-75.4"
                />
                <circle
                  cx="50"
                  cy="50"
                  r="40"
                  fill="none"
                  stroke="#06b6d4"
                  strokeWidth="20"
                  strokeDasharray="50.2 251.2"
                  strokeDashoffset="-138.2"
                />
                <circle
                  cx="50"
                  cy="50"
                  r="40"
                  fill="none"
                  stroke="#10b981"
                  strokeWidth="20"
                  strokeDasharray="62.8 251.2"
                  strokeDashoffset="-188.4"
                />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Payment Chart */}
      <div className="bg-[#141414] border border-gray-800 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-white mb-6">Payment Required vs Free Migrations</h3>
        <div className="space-y-4">
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-400">Free</span>
              <span className="text-sm font-medium text-white">68%</span>
            </div>
            <div className="h-16 bg-blue-500 rounded"></div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-400">Payment Required</span>
              <span className="text-sm font-medium text-white">32%</span>
            </div>
            <div className="h-16 bg-cyan-400 rounded" style={{ width: '65%' }}></div>
          </div>
        </div>
      </div>

      {/* Recent Migration History */}
      <div className="bg-[#141414] border border-gray-800 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-white mb-6">Recent Migration History</h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-800">
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Migration ID</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Database Type</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Source</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Destination</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Status</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Timestamp</th>
              </tr>
            </thead>
            <tbody>
              {recentMigrations.map((migration, index) => (
                <tr
                  key={index}
                  className="border-b border-gray-800/50 hover:bg-gray-800/30 transition-colors"
                >
                  <td className="py-4 px-4 text-sm text-white font-mono">{migration.id}</td>
                  <td className="py-4 px-4 text-sm text-white">{migration.dbType}</td>
                  <td className="py-4 px-4 text-sm text-white">{migration.source}</td>
                  <td className="py-4 px-4 text-sm text-white">{migration.destination}</td>
                  <td className="py-4 px-4">
                    <span
                      className={`inline-flex px-3 py-1 rounded-full text-xs font-medium ${getStatusBg(
                        migration.status
                      )} ${getStatusColor(migration.status)}`}
                    >
                      {migration.status}
                    </span>
                  </td>
                  <td className="py-4 px-4 text-sm text-gray-400 flex items-center gap-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    {migration.timestamp}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Footer */}
      <footer className="text-center text-sm text-gray-500 py-4">
        © 2024 VectorMigrate. All rights reserved.
      </footer>
    </div>
  );
};

export default Overview;